import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Music, Mic, Trash2, Copy, Building2, Clock, ChevronRight, ChevronDown, X, Users, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { useBusiness } from '../context/BusinessContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Genre configuration for tiles - Japandi colors
const GENRE_CONFIG = {
  'Pop': { color: 'from-[#e94560] to-[#d63d56]' },
  'Rock': { color: 'from-[#c49a6c] to-[#b48a5c]' },
  'Jazz': { color: 'from-[#c4a35a] to-[#a68b4b]' },
  'Classical': { color: 'from-[#8b9a7d] to-[#7a8a6c]' },
  'Electronic': { color: 'from-[#6b8e8e] to-[#5b7e7e]' },
  'Hip Hop': { color: 'from-[#9b8b7d] to-[#8b7b6d]' },
  'Country': { color: 'from-[#c49a6c] to-[#a47a4c]' },
  'R&B': { color: 'from-[#b5838d] to-[#a5737d]' },
  'Ambient': { color: 'from-[#7d8597] to-[#6d7587]' },
  'Other': { color: 'from-[#9b8b7d] to-[#7b6b5d]' }
};

const ANNOUNCEMENT_TYPES = ['Store Opening', 'Store Closing', 'Promotion', 'Safety', 'General', 'Holiday', 'Event', 'Other'];
const ANNOUNCEMENT_CONFIG = {
  'Store Opening': { color: 'from-[#8b9a7d] to-[#7a8a6c]', icon: '🌅' },
  'Store Closing': { color: 'from-[#6b8e8e] to-[#5b7e7e]', icon: '🌙' },
  'Promotion': { color: 'from-[#c4a35a] to-[#a68b4b]', icon: '🎉' },
  'Safety': { color: 'from-[#e94560] to-[#d63d56]', icon: '⚠️' },
  'General': { color: 'from-[#7d8597] to-[#6d7587]', icon: '📢' },
  'Holiday': { color: 'from-[#b5838d] to-[#a5737d]', icon: '🎄' },
  'Event': { color: 'from-[#c49a6c] to-[#b48a5c]', icon: '🎪' },
  'Other': { color: 'from-[#9b8b7d] to-[#8b7b6d]', icon: '📋' }
};

const PlaylistTemplates = () => {
  const { businesses } = useBusiness();
  const { isLight } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [templateAssignments, setTemplateAssignments] = useState({});
  const [songs, setSongs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [viewAssignmentsOpen, setViewAssignmentsOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    items: []
  });
  const [expandedGenres, setExpandedGenres] = useState({});
  const [expandedAnnouncementTypes, setExpandedAnnouncementTypes] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [templatesRes, songsRes, announcementsRes, genresRes] = await Promise.all([
        axios.get(`${API}/playlists/templates`, { headers }),
        axios.get(`${API}/songs`, { headers }),
        axios.get(`${API}/announcements`, { headers }),
        axios.get(`${API}/genres`, { headers })
      ]);

      const templatesList = templatesRes.data;
      setTemplates(templatesList);
      setSongs(songsRes.data);
      setAnnouncements(announcementsRes.data);
      setGenres(genresRes.data);
      
      // Fetch assignments for each template
      const assignmentsMap = {};
      for (const template of templatesList) {
        try {
          const assignRes = await axios.get(`${API}/playlists/templates/${template.id}/assignments`, { headers });
          assignmentsMap[template.id] = assignRes.data;
        } catch (err) {
          assignmentsMap[template.id] = [];
        }
      }
      setTemplateAssignments(assignmentsMap);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/playlists/templates/${templateId}/assignments`, { headers });
      setTemplateAssignments(prev => ({ ...prev, [templateId]: res.data }));
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    }
  };

  const handleUnassign = async (templateId, businessId, businessName) => {
    if (!window.confirm(`Remove assignment from ${businessName}? This will delete their copy of the playlist.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/playlists/templates/${templateId}/assignments/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Unassigned from ${businessName}`);
      fetchAssignments(templateId);
    } catch (error) {
      toast.error('Failed to unassign');
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error('Please enter template name');
      return;
    }
    if (newTemplate.items.length === 0) {
      toast.error('Please add at least one song or announcement');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/playlists`, {
        name: newTemplate.name,
        items: newTemplate.items,
        time_slots: [],
        create_as_template: true,
        assign_to_businesses: []
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Template created!');
      setCreateOpen(false);
      setNewTemplate({ name: '', items: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleAssignTemplate = async () => {
    if (!selectedTemplate || selectedBusinesses.length === 0) {
      toast.error('Please select at least one business');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/playlists`, {
        name: selectedTemplate.name,
        items: selectedTemplate.items,
        time_slots: [],
        from_existing_template_id: selectedTemplate.id,
        assign_to_businesses: selectedBusinesses
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`Template assigned to ${selectedBusinesses.length} business(es)!`);
      setAssignOpen(false);
      setSelectedTemplate(null);
      setSelectedBusinesses([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template? This will not affect copies already assigned to businesses.')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/playlists/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleAddItem = (item, type) => {
    const newItem = {
      id: item.id,
      type,
      name: item.name,
      artist: item.artist || '',
      duration: item.duration || (type === 'song' ? 180 : 30),
      itemId: `${item.id}-${Date.now()}`
    };
    setNewTemplate(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const handleRemoveItem = (itemId) => {
    setNewTemplate(prev => ({
      ...prev,
      items: prev.items.filter(item => item.itemId !== itemId)
    }));
  };

  const handleMoveItemUp = (index) => {
    if (index === 0) return;
    setNewTemplate(prev => {
      const items = [...prev.items];
      [items[index - 1], items[index]] = [items[index], items[index - 1]];
      return { ...prev, items };
    });
  };

  const handleMoveItemDown = (index) => {
    if (index === newTemplate.items.length - 1) return;
    setNewTemplate(prev => {
      const items = [...prev.items];
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
      return { ...prev, items };
    });
  };

  const toggleGenre = (genreName) => {
    setExpandedGenres(prev => ({ ...prev, [genreName]: !prev[genreName] }));
  };

  const toggleAnnouncementType = (type) => {
    setExpandedAnnouncementTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const toggleBusinessSelection = (businessId) => {
    setSelectedBusinesses(prev => 
      prev.includes(businessId) 
        ? prev.filter(id => id !== businessId)
        : [...prev, businessId]
    );
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const calculateTotalDuration = (items) => {
    return items.reduce((total, item) => total + (item.duration || 0), 0);
  };

  // Get genre names from the genres prop
  const genreNames = genres.map(g => typeof g === 'string' ? g : g.name);
  
  // Group songs by genre
  const songsByGenre = genreNames.reduce((acc, genreName) => {
    acc[genreName] = songs.filter(song => song.genre === genreName);
    return acc;
  }, {});

  // Group announcements by type
  const announcementsByType = ANNOUNCEMENT_TYPES.reduce((acc, type) => {
    acc[type] = announcements.filter(a => (a.genre || 'General') === type);
    return acc;
  }, {});

  // Helper to get genre config
  const getGenreConfig = (genreName) => {
    const genre = genres.find(g => (typeof g === 'string' ? g : g.name) === genreName);
    if (genre && typeof genre === 'object') {
      return { color: genre.color };
    }
    return GENRE_CONFIG[genreName] || GENRE_CONFIG['Other'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={`animate-spin w-8 h-8 border-2 border-t-transparent rounded-full ${isLight ? 'border-[#e94560]' : 'border-violet-500'}`} />
      </div>
    );
  }

  return (
    <div className={`p-8 min-h-full ${isLight ? 'bg-[#faf8f5]' : 'bg-slate-950'}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-3xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>Playlist Templates</h1>
            <p className={`mt-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Create templates and assign them to businesses</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className={`rounded-full px-6 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-violet-600 hover:bg-violet-500'} text-white`}>
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent className={`max-w-2xl max-h-[80vh] overflow-y-auto ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
              <DialogHeader>
                <DialogTitle className={isLight ? 'text-gray-800' : ''}>Create New Template</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Template Name</Label>
                  <Input
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    placeholder="e.g., Morning Vibes"
                    className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                  />
                </div>

                {/* Genre-based Song Selection */}
                <div>
                  <Label className={`flex items-center gap-2 mb-2 ${isLight ? 'text-gray-600' : ''}`}>
                    <Music className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                    Add Songs by Genre
                  </Label>
                  <div className={`border rounded-lg overflow-hidden max-h-56 overflow-y-auto ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
                    {songs.length === 0 ? (
                      <p className={`text-sm p-4 text-center ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>No songs available</p>
                    ) : (
                      <div className={`divide-y ${isLight ? 'divide-[#ebe7e0]' : 'divide-slate-800'}`}>
                        {genreNames.map((genreName) => {
                          const genreSongs = songsByGenre[genreName] || [];
                          const config = getGenreConfig(genreName);
                          const isExpanded = expandedGenres[genreName];
                          const songCount = genreSongs.length;
                          
                          if (songCount === 0) return null;
                          
                          return (
                            <div key={genreName}>
                              <button
                                type="button"
                                onClick={() => toggleGenre(genreName)}
                                className={`w-full flex items-center gap-3 p-2 transition-colors ${isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/50'}`}
                              >
                                <div 
                                  className={`w-12 h-8 rounded overflow-hidden bg-gradient-to-r ${config.color} flex items-center justify-center flex-shrink-0`}
                                >
                                  <Music className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1 text-left">
                                  <span className={`text-sm font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{genreName}</span>
                                  <span className={`text-xs ml-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>({songCount} songs)</span>
                                </div>
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-slate-400" />
                                )}
                              </button>

                              {isExpanded && (
                                <div className={`border-t ${isLight ? 'bg-gray-50/50 border-[#ebe7e0]' : 'bg-slate-950/50 border-slate-800'}`}>
                                  {genreSongs.map((song) => (
                                    <div key={song.id} className={`flex items-center justify-between py-2 px-4 ${isLight ? 'hover:bg-gray-100' : 'hover:bg-slate-800/30'}`}>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm truncate ${isLight ? 'text-gray-700' : 'text-slate-300'}`}>{song.name}</p>
                                        <p className={`text-xs truncate ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{song.artist} • {formatDuration(song.duration || 180)}</p>
                                      </div>
                                      <Button
                                        type="button"
                                        onClick={() => handleAddItem(song, 'song')}
                                        className={`h-7 px-3 text-xs ml-2 flex-shrink-0 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'}`}
                                      >
                                        Add
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Announcement Type Selection */}
                <div>
                  <Label className={`flex items-center gap-2 mb-2 ${isLight ? 'text-gray-600' : ''}`}>
                    <Mic className={`w-4 h-4 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`} />
                    Add Announcements by Type
                  </Label>
                  <div className={`border rounded-lg overflow-hidden max-h-40 overflow-y-auto ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
                    {announcements.length === 0 ? (
                      <p className={`text-sm p-4 text-center ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>No announcements available</p>
                    ) : (
                      <div className={`divide-y ${isLight ? 'divide-[#ebe7e0]' : 'divide-slate-800'}`}>
                        {ANNOUNCEMENT_TYPES.map((type) => {
                          const typeAnnouncements = announcementsByType[type];
                          const config = ANNOUNCEMENT_CONFIG[type] || ANNOUNCEMENT_CONFIG['Other'];
                          const isExpanded = expandedAnnouncementTypes[type];
                          const count = typeAnnouncements.length;
                          
                          if (count === 0) return null;
                          
                          return (
                            <div key={type}>
                              <button
                                type="button"
                                onClick={() => toggleAnnouncementType(type)}
                                className={`w-full flex items-center gap-3 p-2 transition-colors ${isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/50'}`}
                              >
                                <div 
                                  className={`w-12 h-8 rounded bg-gradient-to-r ${config.color} flex items-center justify-center flex-shrink-0`}
                                >
                                  <span className="text-sm">{config.icon}</span>
                                </div>
                                <div className="flex-1 text-left">
                                  <span className={`text-sm font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{type}</span>
                                  <span className={`text-xs ml-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>({count})</span>
                                </div>
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-slate-400" />
                                )}
                              </button>

                              {isExpanded && (
                                <div className={`border-t ${isLight ? 'bg-gray-50/50 border-[#ebe7e0]' : 'bg-slate-950/50 border-slate-800'}`}>
                                  {typeAnnouncements.map((announcement) => (
                                    <div key={announcement.id} className={`flex items-center justify-between py-2 px-4 ${isLight ? 'hover:bg-gray-100' : 'hover:bg-slate-800/30'}`}>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm truncate ${isLight ? 'text-gray-700' : 'text-slate-300'}`}>{announcement.name}</p>
                                        <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{formatDuration(announcement.duration || 30)}</p>
                                      </div>
                                      <Button
                                        type="button"
                                        onClick={() => handleAddItem(announcement, 'announcement')}
                                        className={`h-7 px-3 text-xs ml-2 flex-shrink-0 ${isLight ? 'bg-[#c4a35a] hover:bg-[#b3944f]' : 'bg-amber-600 hover:bg-amber-500'}`}
                                      >
                                        Add
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Playlist Items */}
                {newTemplate.items.length > 0 && (
                  <div className={`border rounded-lg p-4 ${isLight ? 'border-[#ebe7e0] bg-[#faf8f5]' : 'border-slate-700 bg-slate-800/30'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Label className={isLight ? 'text-gray-600' : ''}>Template Items ({newTemplate.items.length})</Label>
                      <span className={`text-sm ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>Total: {formatDuration(calculateTotalDuration(newTemplate.items))}</span>
                    </div>
                    <p className={`text-xs mb-3 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      Times will auto-calculate when scheduled
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {newTemplate.items.map((item, index) => (
                        <div key={item.itemId} className={`flex items-center justify-between p-2 rounded ${isLight ? 'bg-white border border-[#ebe7e0]' : 'bg-slate-900/50'}`}>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className={`text-xs font-mono ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{index + 1}.</span>
                            {item.type === 'song' ? (
                              <Music className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
                            ) : (
                              <Mic className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`} />
                            )}
                            <span className={`text-sm truncate ${isLight ? 'text-gray-700' : 'text-slate-300'}`}>
                              {item.type === 'song' ? `${item.name} - ${item.artist}` : item.name}
                            </span>
                            <span className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-600'}`}>
                              ({formatDuration(item.duration || (item.type === 'song' ? 180 : 30))})
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              onClick={() => handleMoveItemUp(index)}
                              disabled={index === 0}
                              variant="ghost"
                              className="h-6 w-6 p-0 disabled:opacity-30"
                              title="Move up"
                            >
                              <svg className={`w-3 h-3 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </Button>
                            <Button
                              onClick={() => handleMoveItemDown(index)}
                              disabled={index === newTemplate.items.length - 1}
                              variant="ghost"
                              className="h-6 w-6 p-0 disabled:opacity-30"
                              title="Move down"
                            >
                              <svg className={`w-3 h-3 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </Button>
                            <Button
                              onClick={() => handleRemoveItem(item.itemId)}
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              title="Remove"
                            >
                              <X className={`w-3 h-3 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className={`flex justify-end gap-3 pt-4 border-t ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
                  <Button variant="ghost" onClick={() => setCreateOpen(false)} className={isLight ? 'text-gray-600' : ''}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateTemplate}
                    disabled={!newTemplate.name.trim() || newTemplate.items.length === 0}
                    className={isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-violet-600 hover:bg-violet-500'}
                  >
                    Create Template
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <div className={`text-center py-16 border rounded-xl ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800'}`}>
            <Music className={`w-12 h-12 mx-auto mb-4 ${isLight ? 'text-gray-300' : 'text-slate-600'}`} />
            <h3 className={`text-lg font-medium ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>No templates yet</h3>
            <p className={`mt-1 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Create your first template to share with businesses</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => {
              const songCount = template.items?.filter(i => i.type === 'song').length || 0;
              const annCount = template.items?.filter(i => i.type === 'announcement').length || 0;
              const totalDuration = calculateTotalDuration(template.items || []);
              const assignments = templateAssignments[template.id] || [];

              return (
                <div
                  key={template.id}
                  className={`p-5 rounded-xl border transition-colors ${isLight ? 'border-[#ebe7e0] bg-white hover:border-[#e94560]/50' : 'border-slate-800 bg-slate-900/50 hover:border-violet-600/50'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className={`font-semibold ${isLight ? 'text-gray-800' : 'text-white'}`}>{template.name}</h3>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setAssignOpen(true);
                        }}
                        className={`h-8 w-8 p-0 ${isLight ? 'text-[#e94560] hover:text-[#d63d56]' : 'text-violet-400 hover:text-violet-300'}`}
                        title="Assign to businesses"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                        title="Delete template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className={`flex items-center gap-4 text-sm mb-3 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                    <div className="flex items-center gap-1">
                      <Music className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                      {songCount} songs
                    </div>
                    <div className="flex items-center gap-1">
                      <Mic className={`w-4 h-4 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`} />
                      {annCount} ann.
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                      {formatDuration(totalDuration)}
                    </div>
                  </div>

                  {/* Assigned Businesses Section */}
                  <div className={`border-t pt-3 mt-2 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs flex items-center gap-1 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                        <Users className="w-3 h-3" />
                        Assigned to {assignments.length} business{assignments.length !== 1 ? 'es' : ''}
                      </span>
                      {assignments.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setViewAssignmentsOpen(true);
                          }}
                          className={`text-xs h-6 px-2 ${isLight ? 'text-[#e94560] hover:text-[#d63d56]' : 'text-violet-400 hover:text-violet-300'}`}
                        >
                          Manage
                        </Button>
                      )}
                    </div>
                    {assignments.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {assignments.slice(0, 3).map(a => (
                          <span 
                            key={a.business_id}
                            className={`px-2 py-0.5 text-xs rounded ${isLight ? 'bg-[#8b9a7d]/10 border border-[#8b9a7d]/30 text-[#8b9a7d]' : 'bg-emerald-600/20 border border-emerald-600/30 text-emerald-400'}`}
                          >
                            {a.business_name}
                          </span>
                        ))}
                        {assignments.length > 3 && (
                          <span className={`px-2 py-0.5 text-xs rounded ${isLight ? 'bg-gray-100 text-gray-500' : 'bg-slate-700 text-slate-400'}`}>
                            +{assignments.length - 3} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className={`text-xs italic ${isLight ? 'text-gray-400' : 'text-slate-600'}`}>Not assigned yet</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Assign Template Dialog */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className={`max-w-md ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
            <DialogHeader>
              <DialogTitle className={isLight ? 'text-gray-800' : ''}>Assign Template to Businesses</DialogTitle>
            </DialogHeader>
            {selectedTemplate && (
              <div className="space-y-4 mt-4">
                <div className={`p-3 rounded-lg ${isLight ? 'bg-[#faf8f5]' : 'bg-slate-800/50'}`}>
                  <p className={`text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Assigning template:</p>
                  <p className={`font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{selectedTemplate.name}</p>
                </div>

                <div>
                  <Label className={`mb-2 block ${isLight ? 'text-gray-600' : ''}`}>Select Businesses</Label>
                  <div className={`border rounded-lg max-h-48 overflow-y-auto ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}>
                    {businesses.map((business) => (
                      <label
                        key={business.id}
                        className={`flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 ${isLight ? 'hover:bg-gray-50 border-[#ebe7e0]' : 'hover:bg-slate-800/50 border-slate-800'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedBusinesses.includes(business.id)}
                          onChange={() => toggleBusinessSelection(business.id)}
                          className={`w-4 h-4 rounded ${isLight ? 'border-gray-300 bg-white text-[#e94560] focus:ring-[#e94560]' : 'border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500'}`}
                        />
                        <Building2 className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                        <span className={isLight ? 'text-gray-700' : 'text-slate-200'}>{business.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setAssignOpen(false)}
                    className={`flex-1 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAssignTemplate}
                    disabled={selectedBusinesses.length === 0}
                    className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-violet-600 hover:bg-violet-500'}`}
                  >
                    Assign to {selectedBusinesses.length} Business{selectedBusinesses.length !== 1 ? 'es' : ''}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View/Manage Assignments Dialog */}
        <Dialog open={viewAssignmentsOpen} onOpenChange={setViewAssignmentsOpen}>
          <DialogContent className={`max-w-lg ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}`}>
            <DialogHeader>
              <DialogTitle className={isLight ? 'text-gray-800' : ''}>Manage Assignments</DialogTitle>
              <DialogDescription className={isLight ? 'text-gray-500' : 'text-slate-400'}>
                View and remove business assignments for this template
              </DialogDescription>
            </DialogHeader>
            
            {selectedTemplate && (
              <div className="space-y-4 mt-4">
                <div className={`p-3 rounded-lg ${isLight ? 'bg-[#faf8f5]' : 'bg-slate-800/50'}`}>
                  <p className={`text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Template:</p>
                  <p className={`font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>{selectedTemplate.name}</p>
                </div>

                <div>
                  <Label className={`mb-2 block ${isLight ? 'text-gray-600' : ''}`}>Assigned Businesses</Label>
                  {(templateAssignments[selectedTemplate.id] || []).length === 0 ? (
                    <div className={`text-center py-8 border rounded-lg ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
                      <Building2 className={`w-8 h-8 mx-auto mb-2 ${isLight ? 'text-gray-300' : 'text-slate-600'}`} />
                      <p className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>No businesses assigned yet</p>
                    </div>
                  ) : (
                    <div className={`border rounded-lg divide-y ${isLight ? 'border-[#ebe7e0] divide-[#ebe7e0]' : 'border-slate-700 divide-slate-800'}`}>
                      {(templateAssignments[selectedTemplate.id] || []).map(assignment => (
                        <div 
                          key={assignment.business_id}
                          className={`flex items-center justify-between p-3 ${isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/30'}`}
                        >
                          <div className="flex items-center gap-3">
                            <Building2 className={`w-5 h-5 ${isLight ? 'text-[#8b9a7d]' : 'text-emerald-400'}`} />
                            <div>
                              <p className={`font-medium ${isLight ? 'text-gray-700' : 'text-slate-200'}`}>{assignment.business_name}</p>
                              <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                                {assignment.items_count} items in their copy
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnassign(selectedTemplate.id, assignment.business_id, assignment.business_name)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 px-3"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Unassign
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setViewAssignmentsOpen(false)}
                    className={`flex-1 ${isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}`}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      setViewAssignmentsOpen(false);
                      setAssignOpen(true);
                    }}
                    className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-violet-600 hover:bg-violet-500'}`}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Assign More
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PlaylistTemplates;
